/**
 * AI Stack Strategy — Trading UI client (Phase 12BI).
 *
 * Same-origin /api/v1 fetches. Read-only. No order paths. No admin
 * diagnostics. The deployment_status field carries the live-execution
 * gate; Adversyn never enables live trading on its own.
 */
import { apiV1Base } from "./apiConfig";

export type AIStackPosture =
  | "BUY_SETUP"
  | "LONG_CANDIDATE"
  | "SELL_SIGNAL"
  | "SHORT_CANDIDATE"
  | "REDUCE_EXPOSURE"
  | "TAKE_PROFIT"
  | "HEDGE"
  | "PAIR_TRADE"
  | "OPTIONS_DEFINED_RISK"
  | "WATCH"
  | "WAIT_FOR_PULLBACK"
  | "AVOID_CHASE"
  | "NO_TRADE"
  | "UNAVAILABLE";

export type DeploymentStatus =
  | "VALIDATION_MODE"
  | "LIVE_ELIGIBLE"
  | "LIVE_BLOCKED_BY_DEPLOYMENT_GATE";

export interface AIStackSignal {
  symbol: string;
  company_name: string | null;
  primary_layer: string;
  layers: string[];
  ai_stack_score: number;
  posture: AIStackPosture;
  direction: "BULLISH" | "BEARISH" | "NEUTRAL";
  risk_level: "LOW" | "MEDIUM" | "HIGH" | "EXTREME";
  confidence: "HIGH" | "MEDIUM" | "LOW";
  current_price: number | null;
  change_pct: number | null;
  deployment_status: DeploymentStatus;
  catalyst_status: string;
  catalyst_direction_hint?: string;
  why_signal: string[];
  risks: string[];
  suggested_position_types: string[];
  top_catalysts?: Array<{
    headline: string;
    source: string;
    freshness: string;
    direction_hint: string;
    catalyst_tags: string[];
  }>;
  execution_note: string;
}

export interface AIStackHealthPayload {
  feature: string;
  signal_count: number;
  layer_count: number;
  catalyst_provider_state?: string | null;
  catalyst_source?: string | null;
  deployment_status_counts?: Record<string, number>;
  memory_storage_attestation: {
    memory_storage_symbols: string[];
    memory_storage_count: number;
    mu_present: boolean;
  };
  source_health: {
    equity_snapshot: { data_status: string; stored_at: string | null };
    catalyst_provider_state?: string;
    catalyst_source?: string;
  };
  limitations: string[];
}

export interface AIStackSignalsPayload {
  status: "OK";
  signal_count: number;
  signals: AIStackSignal[];
  top_postures: Record<AIStackPosture, number>;
  catalyst_provider_state?: string | null;
  catalyst_source?: string | null;
  memory_storage_attestation: AIStackHealthPayload["memory_storage_attestation"];
  limitations: string[];
}

type Envelope<T> = { ok: boolean; data: T | null; error?: { message: string; code: string } | null };

async function aiStackGet<T>(path: string): Promise<Envelope<T>> {
  try {
    const res = await fetch(`${apiV1Base()}${path}`, {
      headers: { accept: "application/json" },
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) {
      return { ok: false, data: null, error: { message: `HTTP ${res.status}`, code: String(res.status) } };
    }
    const body = await res.json();
    return body as Envelope<T>;
  } catch (err: any) {
    return { ok: false, data: null, error: { message: err?.message || "network", code: "NETWORK" } };
  }
}

export const getAIStackHealth = () => aiStackGet<AIStackHealthPayload>("/ai-stack-strategy/health");
export const getAIStackSignals = () => aiStackGet<AIStackSignalsPayload>("/ai-stack-strategy/signals?limit=200");
export const getAIStackSymbol = (symbol: string) =>
  aiStackGet<AIStackSignal & { status: string }>(`/ai-stack-strategy/symbol/${encodeURIComponent(symbol)}`);
